import { NgForOf, NgIf, UpperCasePipe } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { NameInitialsPipe } from '../../../core/pipes/name-initals.pipe';
import { ELoadStatus } from '../../../state/state.interface';
import { InlineMenuPositionService } from '../../../utilities/inline-menu-position.service';
import { IBoardMembers, InlineUserChipStore, TInlineUserChipField } from './inline-user-chip.store';

@Component({
  imports: [
    MatIcon,
    NameInitialsPipe,
    NgIf,
    TranslateModule,
    UpperCasePipe,
    MatFormField,
    MatInput,
    MatMenu,
    MatMenuItem,
    MatPrefix,
    NgForOf,
    ReactiveFormsModule,
    FormsModule,
    MatMenuTrigger,
    MatMenuContent,
  ],
  providers: [InlineUserChipStore],
  selector: 'app-inline-user-chip',
  styleUrl: './inline-user-chip.component.scss',
  templateUrl: './inline-user-chip.component.html',
})
export class InlineUserChipComponent implements OnInit, AfterViewInit {
  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;

  @Input({ required: true }) public name!: string | null;
  @Input({ required: true }) public boardId!: number | null;
  @Input({ required: true }) public itemId!: number | null;
  @Input() public field: TInlineUserChipField = 'assignee';
  @Output() triggerRefresh = new EventEmitter<void>();

  public assignedUser = '';
  public unassignedUser = this.translate.instant('page.items.label.unassigned');
  public searchControl = new FormControl('');
  public menuYPosition: 'above' | 'below' = 'below';
  public menuXPosition: 'before' | 'after' = 'after';

  protected readonly ELoadStatus = ELoadStatus;

  constructor(
    public readonly store: InlineUserChipStore,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly inlineMenuPositionService: InlineMenuPositionService,
  ) {}

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((search: string | null) => this.onSearch(search));

    this.store.editItemLoading$.subscribe((status) => {
      if (status === ELoadStatus.success) {
        this.triggerRefresh.emit();
      }
    });
  }

  public ngAfterViewInit(): void {
    this.menuTrigger.menuClosed.subscribe(() => {
      this.searchControl.setValue('');
    });

    this.menuTrigger.menuOpened.subscribe(() => {
      if (this.boardId) {
        this.store.getBoardMembersData({ boardId: this.boardId });
      }
    });
  }

  public selectMenuItem(user: IBoardMembers): void {
    if (user?.id && this.itemId) {
      this.store.editItemUser({
        field: this.field,
        id: this.itemId,
        userId: user?.id,
      });
    }
  }

  public goToUserSetting(): void {
    this.router.navigate(['/settings/users']);
  }

  private onSearch(search: string | null): void {
    this.assignedUser = '';

    if ((search && search.length < 3) || !this.boardId) {
      return;
    }

    this.store.getBoardMembersData({
      boardId: this.boardId,
      ...(search ? { search } : {}),
    });
  }

  public setMenuPosition(triggerElem: HTMLElement): void {
    const { yPosition, xPosition } = this.inlineMenuPositionService.calculateMenuPosition(triggerElem);
    this.menuYPosition = yPosition;
    this.menuXPosition = xPosition;
  }

  public onTriggerClick(triggerElem: HTMLElement): void {
    this.setMenuPosition(triggerElem);

    setTimeout(() => {
      this.menuTrigger.openMenu();
    });
  }
}
